import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import TableService from 'src/shared/api/table/TableService';
import {
  AuditUserInfo,
  MassOperationAuditItem,
  MassOperationAuditOperation,
} from 'src/shared/api/table/types';
import { Button, Modal } from 'src/shared/ui';

interface FormAuditHistoryModalProps {
  onClose: () => void;
  tableId: number;
  formLabel?: string;
}

const operationLabelMap: Record<MassOperationAuditOperation, string> = {
  create: 'Добавление',
  update: 'Изменение',
  delete: 'Удаление',
};

const sourceFlowLabelMap: Record<string, string> = {
  batch: 'Массовая операция',
  batch_subset: 'Операция по выборке',
  unknown: 'Неизвестный источник',
};

const formatDate = (value: string): string => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }
  return parsedDate.toLocaleString('ru-RU');
};

const formatActor = (user?: AuditUserInfo | null, fallbackId?: number | null): string => {
  const normalizedDisplayName = user?.display_name?.trim();
  if (normalizedDisplayName) {
    return normalizedDisplayName;
  }

  const normalizedFullName = user?.full_name?.trim();
  if (normalizedFullName) {
    return normalizedFullName;
  }

  const normalizedUserName = user?.user_name?.trim();
  if (normalizedUserName) {
    return normalizedUserName;
  }

  const normalizedEmail = user?.email?.trim();
  if (normalizedEmail) {
    return normalizedEmail;
  }

  const resolvedId = user?.id ?? fallbackId;
  if (typeof resolvedId === 'number' && resolvedId > 0) {
    return `ID ${resolvedId}`;
  }

  return 'Неизвестный пользователь';
};

const formatOperationLabel = (item: MassOperationAuditItem): string => {
  const normalizedActionLabel = item.action_label?.trim();
  if (normalizedActionLabel) {
    return normalizedActionLabel;
  }

  const normalizedOperation = item.operation?.trim().toLowerCase() as MassOperationAuditOperation;
  return operationLabelMap[normalizedOperation] || item.operation;
};

const formatSourceFlowLabel = (value: string): string => {
  const normalizedValue = value.trim().toLowerCase();
  return sourceFlowLabelMap[normalizedValue] || value;
};

const formatAffectedRows = (item: MassOperationAuditItem): string => {
  if (item.row_range_label?.trim()) {
    return item.row_range_label;
  }

  if (item.row_ranges?.length) {
    return item.row_ranges.join(', ');
  }

  if (item.affected_row_ids?.length) {
    return item.affected_row_ids.join(', ');
  }

  return '-';
};

export const FormAuditHistoryModal: FC<FormAuditHistoryModalProps> = ({
  onClose,
  tableId,
  formLabel,
}) => {
  const [history, setHistory] = useState<MassOperationAuditItem[]>([]);
  const [selectedOperation, setSelectedOperation] = useState<MassOperationAuditOperation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const contextLabel = useMemo(() => {
    const normalizedLabel = formLabel?.trim();
    if (normalizedLabel) {
      return normalizedLabel;
    }

    return `Форма ID ${tableId}`;
  }, [formLabel, tableId]);

  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const { data } = await TableService.getMassOperationAudit(tableId, {
        limit: 100,
        operation: selectedOperation || undefined,
      });
      setHistory(data || []);
    } catch (error) {
      console.error('Не удалось загрузить журнал аудита', error);
      setErrorMessage('Не удалось загрузить журнал аудита');
    } finally {
      setIsLoading(false);
    }
  }, [selectedOperation, tableId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const handleToggleOperation = (operation: MassOperationAuditOperation) => {
    setSelectedOperation(currentOperation => (currentOperation === operation ? null : operation));
  };

  return (
    <Modal onClose={onClose}>
      <div className="flex w-[56rem] max-w-[90dvw] flex-col">
        <div className="font-gothampro-500 mb-2 text-2xl text-green-100">Журнал аудита</div>
        <div className="font-gothampro-400 mb-5 text-sm dark:text-white">{contextLabel}</div>

        <div className="mb-5 flex flex-wrap items-center gap-2">
          {(['create', 'update', 'delete'] as MassOperationAuditOperation[]).map(operation => (
            <Button
              key={operation}
              size="small"
              classNames="w-36"
              variant={selectedOperation === operation ? 'violet' : 'greyLight'}
              onClick={() => handleToggleOperation(operation)}
            >
              {operationLabelMap[operation]}
            </Button>
          ))}

          <div className="ml-auto flex items-center gap-2">
            <div className="text-extra-xs opacity-70 dark:text-white">
              {selectedOperation
                ? `Фильтр: ${operationLabelMap[selectedOperation]}`
                : 'Фильтр: все операции'}
            </div>
            <Button
              size="small"
              classNames="w-32"
              variant="greyLight"
              onClick={() => void loadHistory()}
              disabled={isLoading}
            >
              Обновить
            </Button>
          </div>
        </div>

        <div className="max-h-[32rem] overflow-y-auto rounded border border-grey-1000 p-2">
          {isLoading && (
            <div className="font-gothampro-400 p-2 text-sm opacity-70 dark:text-white">
              Загрузка журнала аудита...
            </div>
          )}

          {!isLoading && errorMessage && (
            <div className="p-2">
              <div className="font-gothampro-400 text-sm text-red-400">{errorMessage}</div>
              <Button
                size="small"
                classNames="mt-3 w-32"
                variant="violetOutline"
                onClick={() => void loadHistory()}
              >
                Повторить
              </Button>
            </div>
          )}

          {!isLoading && !errorMessage && !history.length && (
            <div className="font-gothampro-400 p-2 text-sm opacity-70 dark:text-white">
              Аудит пока пуст
            </div>
          )}

          {!isLoading &&
            !errorMessage &&
            history.map(item => (
              <div key={item.id} className="mb-2 rounded border border-grey-1000 p-3">
                <div className="mb-1 text-extra-xs opacity-60 dark:text-white">
                  {formatDate(item.changed_at)}
                </div>

                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <div className="rounded-full bg-blue-rgba-100 px-3 py-1 text-extra-xs text-blue-1500">
                    {formatOperationLabel(item)}
                  </div>
                  <div className="rounded-full bg-grey-1400 px-3 py-1 text-extra-xs text-white">
                    {`${item.affected_count} строк`}
                  </div>
                </div>

                <div className="mb-1 text-extra-xs opacity-70 dark:text-white">
                  Пользователь: {formatActor(item.changed_by_user, item.changed_by)}
                </div>

                <div className="mb-2 text-extra-xs opacity-70 dark:text-white">
                  Источник: {formatSourceFlowLabel(item.source_flow)}
                </div>

                <div className="font-gothampro-400 text-sm dark:text-white">
                  <span className="opacity-70">Затронутые строки:</span> {formatAffectedRows(item)}
                </div>
              </div>
            ))}
        </div>
      </div>
    </Modal>
  );
};
